import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FeatureFlag, useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { isPushSupported } from '../../../services/pushNotificationService';

interface PushDetail {
  title?: string;
  body?: string;
  data?: Record<string, string>;
}

interface MessageDetail {
  conversationSid: string;
  body?: string;
  author?: string;
}

/**
 * Toasts in-app pour messages/notifications reçus en foreground — natif uniquement.
 */
const NativeForegroundMessageToast: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFeatureEnabled } = useFeatureFlags();
  const chatOn = isFeatureEnabled(FeatureFlag.CHAT_FEATURE);

  useEffect(() => {
    if (!isPushSupported()) return;

    const showMessageToast = (
      title: string,
      body: string,
      conversationSid?: string,
    ) => {
      if (!chatOn) return;

      const onMessaging =
        location.pathname === '/messaging' &&
        (!conversationSid ||
          location.search.includes(conversationSid) ||
          !location.search.includes('conversation='));

      if (onMessaging && conversationSid && location.search.includes(conversationSid)) {
        return;
      }

      toast.info(
        <button
          type="button"
          className="native-fg-toast"
          onClick={() => {
            const path = conversationSid
              ? `/messaging?conversation=${conversationSid}`
              : '/messaging';
            navigate(path);
            toast.dismiss();
          }}
        >
          <strong>{title}</strong>
          {body ? <span>{body}</span> : null}
        </button>,
        { autoClose: 5000, closeOnClick: true },
      );
    };

    const onPush = (e: Event) => {
      const detail = (e as CustomEvent<PushDetail>).detail;
      const data = detail?.data;
      const sid = data?.conversation_sid;
      const title = detail?.title ?? 'Notification';
      const body = detail?.body ?? '';
      if (data?.type === 'new_message' || sid) {
        showMessageToast(title, body, sid);
        return;
      }
      toast.info(body || title, { autoClose: 4000 });
    };

    const onMessage = (e: Event) => {
      if (!chatOn) return;
      const detail = (e as CustomEvent<MessageDetail>).detail;
      if (!detail?.conversationSid) return;
      showMessageToast(
        'Nouveau message',
        detail.body?.slice(0, 80) || 'Vous avez reçu un message',
        detail.conversationSid,
      );
    };

    window.addEventListener('native-push-received', onPush);
    window.addEventListener('native-message-received', onMessage);
    return () => {
      window.removeEventListener('native-push-received', onPush);
      window.removeEventListener('native-message-received', onMessage);
    };
  }, [chatOn, location.pathname, location.search, navigate]);

  return null;
};

export default NativeForegroundMessageToast;
